import produce from 'immer';
import { registerReducer } from '../../StateSetup/RootReducer';
import FEATURES from '../features';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/getEntitlements';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Gets entitlements from gb-core-2 based on the features that are tied to an entitlement
 */
export const GetEntitlements = () => {
    return async (dispatch, getState, { logger }) => {
        const { jwt, sessionCorrelationId } = getState().Authentication;
        const transactionId = logger.generateGuid();

        dispatch({ type: REQUEST_ACTION_NAME });

        logger.info(`${ACTION_NAME_PREFIX}: Getting member entitlements.`, {
            correlation: { transactionId },
        });

        const getEntitlements = await fetch(`${BFF_URL}/api/v2`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'gb-authorization': jwt,
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
                'gb-client-name': GBCORE_CLIENT_NAME,
                'gb-client-version': GBCORE_CLIENT_VERSION,
            },
            body: JSON.stringify({
                operationName: 'EntitlementsQuery',
                variables: {
                    // Only send request for features which have an attached entitlement
                    queries: FEATURES.filter((feature) => feature.hasEntitlement).map((feature) => ({
                        resource: feature.entitlementName,
                        action: feature.entitlementValueCheck,
                    })),
                },
                query: `
                    query EntitlementsQuery($queries: [EntitlementQuery]) {
                        memberEntitlements(queries: $queries) {
                            resource
                            decision
                            action
                        }
                    }
            `,
            }),
        });

        const getEntitlementsResponse = await getEntitlements.json();
        return dispatch({
            type: RESPONSE_ACTION_NAME,
            features: FEATURES,
            data: getEntitlementsResponse.data,
            success: true,
        });
    };
};

/**
 * Sets a loading indicator while the entitlements data is loading
 * @param {object} state
 * @param {object} action
 */
export const GetEntitlementsRequestReducer = produce((state) => {
    state.Features.isLoading = true;
});

/**
 * Maps the entitlement response into an object for easy lookup and stores in Redux state
 * @param {object} state
 * @param {object} action
 */
export const GetEntitlementsResponseReducer = produce((state, action) => {
    // Map features to an object for easy lookups
    // Use entitlement value if it exists, otherwise, fallback to feature flag
    const features = {};
    action.features.forEach((feature) => {
        const entitlement = action.data.memberEntitlements.find(
            (e) => e.resource === feature.entitlementName?.toLowerCase() && e.action === feature.entitlementValueCheck
        );
        features[feature.name] = entitlement ? entitlement.decision === 'permit' : feature.value;
    });
    state.Features.isLoading = false;
    state.Features.data = features;
});

// Register Reducers with Redux root reducer
registerReducer(REQUEST_ACTION_NAME, GetEntitlementsRequestReducer);
registerReducer(RESPONSE_ACTION_NAME, GetEntitlementsResponseReducer);
